/**
 * Utility functions for assigning consistent colors to user avatars
 */

// Available avatar colors
const AVATAR_COLORS = [
  '#25205A',
  '#6776A8',
  '#738ED0',
  '#1C408D',
  '#955DE6',
  '#4659E3',
  '#47A4E0',
  '#259896',
  '#29C1B3',
  '#ACB406',
];

/**
 * Simple hash function to convert a string to a number
 * @param str - The string to hash (typically email or name)
 * @returns A hash number
 */
function hashString(str: string): number {
  let hash = 0;
  if (!str || str.length === 0) return hash;

  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  return Math.abs(hash);
}

/**
 * Get a consistent color for a given identifier (email/name)
 * The same identifier will always return the same color
 * @param identifier - User email or name
 * @returns Hex color code
 */
export function getAvatarColor(identifier: string): string {
  if (!identifier || identifier.trim().length === 0) {
    return AVATAR_COLORS[0]; // Default to first color for empty/invalid input
  }

  const hash = hashString(identifier.toLowerCase().trim());
  const colorIndex = hash % AVATAR_COLORS.length;
  return AVATAR_COLORS[colorIndex];
}

/**
 * Get the text color (white or dark) that contrasts well with the background color
 * @param backgroundColor - Hex color code
 * @returns 'white' or '#050320' for optimal contrast
 */
export function getContrastTextColor(backgroundColor: string): string {
  // Remove # if present
  const hex = backgroundColor.replace('#', '');

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return white for dark backgrounds, dark text for light backgrounds
  return luminance > 0.5 ? '#050320' : 'white';
}
